import { useMemo, useState } from 'react'
import { formatAmount, money } from '../../../shared/stash'
import { iconFor, stash, type Pack } from '../../lib/stash'
import { sound } from '../../lib/fun'
import Icon from '../Icon'

/** Every sale, newest first, grouped by day — with an undo for a mis-tap. */
export default function History({ sold, onChange }: { sold: Pack[]; onChange: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null)

  const days = useMemo(() => {
    const m = new Map<string, Pack[]>()
    for (const p of sold) {
      const d = new Date(p.sold_at ?? 0).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
      m.set(d, [...(m.get(d) ?? []), p])
    }
    return [...m.entries()]
  }, [sold])

  async function undo(p: Pack) {
    setError(null)
    try { await stash.unsell(p.id); sound('undo'); await onChange() } catch (e) { setError((e as Error).message) }
  }

  if (!sold.length) {
    return (
      <div className="empty">
        <div className="empty-icon"><Icon name="chart" size={44} /></div>
        <h2>No sales yet</h2>
        <p className="muted">Your first one is worth a badge.</p>
      </div>
    )
  }

  return (
    <div className="history">
      <h1 className="page-title">Sales <small>{sold.length}</small></h1>
      {error && <p className="error">{error}</p>}
      {days.map(([day, list]) => {
        const total = list.reduce((n, p) => n + (p.sold_cents ?? 0), 0)
        const profit = list.reduce((n, p) => n + (p.sold_cents ?? 0) - p.cost_cents, 0)
        return (
          <section key={day} className="day">
            <div className="day-head">
              <b>{day}</b>
              <span><b className="up">{money(total)}</b> <small className="muted">· {money(profit)} profit</small></span>
            </div>
            {list.map((p) => (
              <div key={p.id} className="sale">
                <span className="stack-icon"><Icon name={iconFor(p.category)} /></span>
                <div className="stack-body">
                  <b>{p.label} <span className="muted small">{formatAmount(p.amount_milli, p.unit)}</span></b>
                  <span className="muted small">
                    {new Date(p.sold_at ?? 0).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    {p.sold_note ? ` · ${p.sold_note}` : ''}
                  </span>
                </div>
                <b className="up">{money(p.sold_cents ?? 0)}</b>
                <button type="button" className="ghost small" onClick={() => undo(p)} title="Put it back on the shelf"><Icon name="undo" size={16} /> Undo</button>
              </div>
            ))}
          </section>
        )
      })}
    </div>
  )
}
